/**
 * 本人成績(Phase 4。2026-09-12 着手)。**スマートフォン前提**(D-2)。
 *  - operator: 自分の数字だけ(`users/{uid}.op_id` の人)
 *  - admin: 画面上部でアポインターを選んで、その人の画面をそのまま確認できる
 *    (ログインし直さずにスタッフ画面を見たい、という 2026-09-12 の指示)
 * 数字は日次【集計】と同じ計算(staffMetrics)を使う。ここで独自の計算はしない。
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { HOUR_BANDS, type Company, type DailySummary, type DateStr, type MonthTargets, type Operator } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperators } from '@core/masters/operatorRepo';
import { daysInMonth, fmtDuration, fmtNum, fmtPct, staffMetrics, sumAgg, rankOf, apphOf, derived, type StaffMetrics } from '@core/dashboard/calc';
import { fetchMonthTargets, fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import MiniChart from '@core/dashboard/MiniChart';

const fmtDate = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}`;
const RANK_CLASS: Record<string, string> = { S: 'cf-rank-s', A: 'cf-rank-a', 'B+': 'cf-rank-bp', 'B-': 'cf-rank-bm', C: 'cf-rank-c' };

export default function StaffMyPage() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const today = todayStr();
  const month = today.slice(0, 7);
  const from = `${month}-01` as DateStr;
  const to = `${month}-${String(daysInMonth(month)).padStart(2, '0')}` as DateStr;

  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [targets, setTargets] = useState<MonthTargets | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);

  /** 見る人。admin は ?op= で切り替えられる。operator は自分に固定 */
  const opId = isAdmin ? (params.get('op') ?? '') : (user?.op_id ?? '');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void Promise.all([fetchSummariesRange(from, to), fetchMonthTargets(month), fetchCompanies(), isAdmin ? fetchOperators() : Promise.resolve([] as Operator[])])
      .then(([s, t, c, ops]) => {
        if (!alive) return;
        setSummaries(s);
        setTargets(t);
        setCompanies(c);
        setOperators(ops);
      })
      .catch((e: unknown) => {
        if (alive) toast(e instanceof Error ? e.message : '読み込みに失敗しました');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to, month, isAdmin, toast]);

  const companyMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const myRows = useMemo(() => {
    if (!targets || !opId) return [] as StaffMetrics[];
    return summaries.flatMap((s) => s.staff.filter((st) => st.op_id === opId).map((st) => staffMetrics(st, s, targets, companyMap)));
  }, [summaries, targets, companyMap, opId]);

  /** 直近でデータがある日 */
  const latest = useMemo(() => {
    const ds = myRows.map((r) => r.date);
    return ds.length > 0 ? ds.reduce((a, b) => (a > b ? a : b)) : null;
  }, [myRows]);

  const dayRows = useMemo(() => myRows.filter((r) => r.date === latest), [myRows, latest]);
  const dayAgg = useMemo(() => sumAgg(dayRows), [dayRows]);
  const monthAgg = useMemo(() => sumAgg(myRows), [myRows]);
  const dayD = useMemo(() => derived(dayAgg), [dayAgg]);
  const monthD = useMemo(() => derived(monthAgg), [monthAgg]);
  const business = dayRows[0]?.business ?? myRows[0]?.business ?? 'kyutoki';
  const dayRank = rankOf(apphOf(dayAgg), business);
  const monthRank = rankOf(apphOf(monthAgg), business);
  const name = operators.find((o) => o.id === opId)?.name ?? myRows[0]?.name ?? '';

  /** 日別の取得数(今月。データのある日だけ点を打つ) */
  const trend = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const r of myRows) byDate.set(r.date, (byDate.get(r.date) ?? 0) + r.appointments);
    const labels: string[] = [];
    const values: (number | null)[] = [];
    for (let d = 1; d <= daysInMonth(month); d++) {
      const date = `${month}-${String(d).padStart(2, '0')}`;
      if (date > today) break;
      labels.push(String(d));
      values.push(byDate.has(date) ? (byDate.get(date) ?? 0) : null);
    }
    return { labels, values };
  }, [myRows, month, today]);

  if (loading) {
    return (
      <div className="screen">
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      </div>
    );
  }

  const picker = isAdmin && (
    <div className="card" style={{ marginBottom: 12 }}>
      <h3>アポインターを選ぶ</h3>
      <p className="hint" style={{ marginBottom: 8 }}>本人がログインしたときに見える画面です。ログインし直さずに確認できます。</p>
      <select
        value={opId}
        onChange={(e) => {
          const next = new URLSearchParams(params);
          if (e.target.value) next.set('op', e.target.value);
          else next.delete('op');
          setParams(next, { replace: true });
        }}
        style={{ width: '100%', maxWidth: 340, height: 34 }}
      >
        <option value="">選んでください</option>
        {operators
          .filter((o) => o.status === 'active')
          .map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}({o.op_code})
            </option>
          ))}
      </select>
    </div>
  );

  if (!opId) {
    return (
      <div className="screen">
        {picker}
        {!isAdmin && (
          <div className="card">
            <div className="empty">アカウントにアポインターが紐づいていません。管理者に連絡してください。</div>
          </div>
        )}
      </div>
    );
  }

  const Card = ({ label, value, sub, cls }: { label: string; value: string; sub?: string; cls?: string }) => (
    <div className="cash">
      <div className="nm">{label}</div>
      <div className={`amt ${cls ?? ''}`}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );

  return (
    <div className="screen">
      {picker}

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ marginBottom: 6 }}>
          {name} さんの成績
          <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>{latest ? `最新 ${fmtDate(latest)}` : 'データがまだありません'}</span>
        </h3>
      </div>

      {latest === null ? (
        <div className="card">
          <div className="empty">今月の集計データがまだありません</div>
        </div>
      ) : (
        <>
          <div className="cash-cards">
            <div className="cash total">
              <div className="nm">{fmtDate(latest)}の取得数</div>
              <div className="amt">{dayAgg.appointments}件</div>
              <div className="sub">通話 {dayAgg.calls}回 ／ 取得率 {fmtPct(dayD.hit_rate)}</div>
            </div>
            <Card label="その日のRANK" value={dayRank} cls={RANK_CLASS[dayRank] ?? ''} sub={`見込/h ${fmtNum(apphOf(dayAgg), 2)}`} />
            <Card label="その日の実稼働" value={`${fmtNum(dayAgg.real_hours, 2)} h`} sub={`稼働合計 ${fmtDuration(dayD.work_sec)}`} />
            <Card label="今月の取得数" value={`${monthAgg.appointments}件`} sub={`RANK ${monthRank} ／ 取得率 ${fmtPct(monthD.hit_rate)}`} />
          </div>

          <div className="card">
            <h3>今月の取得数の推移</h3>
            <MiniChart labels={trend.labels} series={[{ key: 'ap', label: '取得数', color: 'var(--azure)', values: trend.values }]} format={(v) => `${v}件`} />
          </div>

          <div className="card">
            <h3>{fmtDate(latest)}の時間帯別の取得数</h3>
            <MiniChart
              type="bar"
              labels={HOUR_BANDS.map((h) => String(h))}
              series={[{ key: 'h', label: '取得数', color: 'var(--azure)', values: HOUR_BANDS.map((_, i) => dayAgg.by_hour[i] ?? 0) }]}
              format={(v) => `${v}件`}
              labelEvery={1}
            />
          </div>

          <div className="card">
            <h3>{fmtDate(latest)}の内訳</h3>
            <div className="table-scroll">
              <table className="fit-table">
                <tbody>
                  <tr>
                    <th style={{ width: '46%' }}>通話回数</th>
                    <td className="num">{dayAgg.calls}回</td>
                  </tr>
                  <tr>
                    <th>通話時間(1通話あたり)</th>
                    <td className="num">{fmtDuration(dayAgg.talk_sec)}({fmtDuration(dayD.talk_avg)})</td>
                  </tr>
                  <tr>
                    <th>受付時間</th>
                    <td className="num">{fmtDuration(dayAgg.reception_sec)}</td>
                  </tr>
                  <tr>
                    <th>後処理時間(1回あたり)</th>
                    <td className="num">{fmtDuration(dayAgg.after_sec)}({fmtDuration(dayD.after_avg)})</td>
                  </tr>
                  <tr>
                    <th>通話/h</th>
                    <td className="num">{fmtNum(dayD.calls_per_hour, 0)}</td>
                  </tr>
                  <tr>
                    <th>1取得あたりの稼働時間</th>
                    <td className="num">{fmtDuration(dayD.work_per_appt)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
